<template>
	<div class="input-view" @click="onClick" :style="_style">
		<input v-if="type == 'string' || type == 'password' || type == 'number'" ref="input" @focus="focus_on" @blur="focus_off" :type="_type" :style="_inputStyle" :value="_value" @input="onInput($event.target.value)" @keyup="$emit('keyup', $event)" />
		<textarea v-if="type == 'text'" ref="input" @focus="focus_on" @blur="focus_off" :style="_inputStyle" :value="_value" @input="onInput($event.target.value)" />
		<div class="label" :style="{
			...theme.typography.body2,
			..._labelStyle
		}">{{ label }}</div>
		<div v-if="suffix" class="suffix" :style="{
			...theme.typography.body2,
			opacity: focus || _chars > 0 ? 1 : 0
		}">{{ suffix }}</div>
		<div v-if="chars" class="chars" :style="{
			...theme.typography.body2
		}">{{ _chars | number(0) }} / {{ chars | number(0) }}</div>
		<div v-if="!inline" class="underline" :style="_underlineStyle"></div>
		<div v-if="!inline" class="underline colored" :style="_underlineColoredStyle"></div>
	</div>
</template>

<script>
	import theme from '@/mixins/theme';
	import focus from '@/mixins/focus';

	export default {
		mixins: [theme, focus],
		data() {
			return {
				valid: true
			};
		},
		props: {
			error: {
				type: Boolean,
				default: false
			},
			type: {
				type: String,
				default: 'string',
				validator(v) {
					return ['string', 'text', 'password', 'number'].indexOf(v) !== -1
				}
			},
			label: {
				type: String,
				default: ''
			},
			chars: {
				type: Number,
				default: null
			},
			value: {
				type: [String, Number],
				default: ''
			},
			colors: {
				type: Object,
				default() {
					return {};
				}
			},
			height: {
				type: Number,
				default: 54
			},
			suffix: {
				type: String,
				default: null
			},
			decimals: {
				type: Number,
				default: 0
			},
			inline: {
				type: Boolean,
				default: false
			}
		},
		watch: {
			value: {
				immediate: true,
				handler(n) {
					if (this.type == 'number') this.valid = n != '' && !isNaN(n);
				}
			}
		},
		computed: {
			_chars() {
				return this.value === null ? '' : this.value.toString().length;
			},
			_value() {
				var val = this.value === null ? '' : this.value;

				if (this.type == 'number' && this.valid && !this.focus) {
					return this.$options.filters.number(val, this.decimals);
				} else if (this.type == 'number') {
					return val.replace('.', this.$options.filters.l('.'));
				}

				return val;
			},
			_type() {
				return ['string', 'number'].indexOf(this.type) > -1 ? 'text' : this.type;
			},
			_colors() {
				return {
					...{
						background: this.theme.colors.backgroundVariant,
						underline: 	'#ccc',
						primary: 	this.theme.colors.primary,
						label:		this.theme.colors.onBackgroundVariant
					},
					...this.colors
				};
			},
			_style() {
				return {
					backgroundColor: 	this.inline ? 'none' : this._colors.background,
					height: 			this.inline ? '30px' : `${this.height}px`
				};
			},
			_inputStyle() {
				return {
					...this.theme.typography.body1,
					height:				this.inline ? '30px' : `${this.height}px`,
					color:				this.valid && !this.error ? 'black' : 'red',
					paddingTop: 		this.inline ? '0' : '18px',
					paddingLeft: 		this.inline ? '0' : '15px'
				};
			},
			_underlineStyle() {
				return {
					backgroundColor: 	this._colors.underline
				};
			},
			_underlineColoredStyle() {
				return {
					transition: 		'left 300ms ' + this.theme.transitions.cubic + ', width 300ms ' + this.theme.transitions.cubic,
					backgroundColor:	this._colors.primary,
					left: 				this.focus ? '0%' : '50%', 
					width: 				this.focus ? '100%' : '0%' 
				};
			},
			_labelStyle() {
				return {
					fontSize:			this.focus || this._value.toString().length > 0 ? '12px' : '16px',
					transition:			'line-height 300ms ' + this.theme.transitions.cubic + ', color 300ms ' + this.theme.transitions.cubic + ', font-size 300ms ' + this.theme.transitions.cubic,
					lineHeight: 		this.focus || this._value.toString().length > 0 ? '30px' : '52px',
					color: 				this.focus ? this._colors.primary : this._colors.label
				};
			}
		},
		methods: {
			onClick() {
				this.$refs.input.focus();
			},
			onInput(value) {
				var val = value;

				if (this.type == 'number') {
					val = val.replace(this.$options.filters.l('.'), '.');
				}

				if (this.chars !== null && this._chars >= this.chars) {
					val = val.substr(0, this.chars);
				}

				this.$emit('input', val);

				this.$forceUpdate();
			}		
		}
	}
</script>

<style lang="scss" scoped>
	.input-view {
		display: inline-block;
		background-color: none;
		box-sizing: border-box;
		position: relative;
		width: 100%;
		cursor: text;

		& > input, & > textarea {
			outline: none;
			background: none;
			border: none;
			width: 100%;
			height: 54px;
			box-sizing: border-box;
			padding: 18px 15px 0 15px;
			position: absolute;
			bottom: 2px;
			line-height: 36px;
			resize: none;
		}

		& > .label {
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			pointer-events: none;
			padding-left: 15px;
		}

		& > .suffix {
			transition: 250ms opacity ease-in-out;
			position: absolute;
			right: 15px;
			top: 18px;
			line-height: 36px;
			pointer-events: none;
			padding-left: 15px;
		}

		& > .chars {
			position: absolute;
			right: 15px;
			top: 0;
			line-height: 30px;
			pointer-events: none;
			font-size: 12px;
		}

		& > .underline {
			position: absolute;
			left: 0;
			bottom: 0;
			width: 100%;
			height: 1px;

			&.colored {
				height: 2px;
			}
		}
	}
</style>